import Phaser from 'phaser';

/**
 * Lightweight WebAudio beeps — no external audio files required.
 * Respects user mute and browser gesture policies via Phaser sound unlock.
 */
export class AudioManager {
  private scene: Phaser.Scene;
  private enabled: boolean;
  private musicEnabled: boolean;
  private music?: Phaser.Sound.BaseSound;

  constructor(scene: Phaser.Scene, enabled = true, musicEnabled = true) {
    this.scene = scene;
    this.enabled = enabled;
    this.musicEnabled = musicEnabled;
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.music?.stop();
  }

  setMusicEnabled(v: boolean): void {
    this.musicEnabled = v;
    if (!v) this.music?.stop();
    else this.startMusic();
  }

  startMusic(): void {
    if (!this.enabled || !this.musicEnabled) return;
    // Procedural loop using a soft tone sequence is optional; keep silent-friendly.
  }

  beep(freq: number, duration = 0.08, type: OscillatorType = 'square', gain = 0.04): void {
    if (!this.enabled) return;
    try {
      const sound = this.scene.sound as Phaser.Sound.WebAudioSoundManager & {
        context?: AudioContext;
      };
      const ctx = sound.context;
      if (!ctx) return;
      if (ctx.state === 'suspended') void ctx.resume();
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      g.gain.value = gain;
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch {
      // Audio unavailable — silent fallback.
    }
  }

  pickup(): void {
    this.beep(880, 0.07, 'triangle', 0.05);
    this.beep(1320, 0.09, 'triangle', 0.04);
  }

  boost(): void {
    this.beep(220, 0.12, 'sawtooth', 0.035);
    this.beep(440, 0.16, 'sawtooth', 0.03);
  }

  crash(): void {
    this.beep(110, 0.18, 'square', 0.05);
    this.beep(70, 0.22, 'sawtooth', 0.04);
  }

  countdown(): void {
    this.beep(520, 0.1, 'square', 0.045);
  }

  go(): void {
    this.beep(660, 0.08, 'triangle', 0.05);
    this.beep(990, 0.14, 'triangle', 0.05);
  }

  nearMiss(): void {
    this.beep(740, 0.05, 'sine', 0.03);
  }
}
